import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, ArrowLeft, Upload, X, Image as ImageIcon, Loader2, Bike, Bus, Truck, ShieldAlert, Clock, XCircle } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { ToastModal } from '../../components/ui/Toast';
import AddressAutocomplete from '../../components/maps/AddressAutocomplete';
import { carAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { compressImage } from '../../lib/utils';
import { useVehicleTypes } from '../../hooks/useVehicleTypes';
import { purposesFor, purposeTitle, OWNER_OPTIONS } from '../../lib/vehiclePurpose';

// Convert number to Indian words (lakhs, crores)
const numberToIndianWords = (num) => {
  if (!num || isNaN(num) || num <= 0) return '';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const numToWords = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
    if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
    if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
    return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
  };

  return numToWords(Math.floor(num)) + ' Rupees Only';
};

const AddCar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { vehicleTypes: VEHICLE_TYPES } = useVehicleTypes();
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  // Owners who only rent (or only sell) skip the chooser; 'both' owners pick first.
  const businessType = user?.businessType || 'both';
  const [purpose, setPurpose] = useState(
    businessType === 'rental' ? 'rental' : businessType === 'sale' ? 'sale' : null
  );
  const forRent = purpose !== 'sale';
  const forSale = purpose !== 'rental';
  const [formData, setFormData] = useState({
    // Vehicle type
    vehicleType: 'car',
    vehicleCategory: 'four-wheeler',
    make: '',
    model: '',
    variant: '',
    // Blank, not the current year. Pre-filling made owners publish "2026" on
    // decade-old vehicles simply by not disagreeing with it.
    year: '',
    registrationNumber: '',
    seats: 5,
    fuelType: 'Petrol',
    transmission: 'Manual',
    // Registration plate/board (India): white=private, yellow=commercial, green=EV, black=self-drive rental
    registrationBoard: 'white',
    permitType: '',
    permitValidTill: '',
    fitnessValidTill: '',
    commercialInsurance: false,
    pricePerDay: '',
    pricePerKm: '',
    city: '',
    state: '',
    latitude: null,
    longitude: null,
    isAvailable: true,
    color: '',
    mileage: '',
    features: '',
    description: '',
    // Service type fields
    serviceTypes: ['rental'],
    forSale: false,
    salePrice: '',
    salePriceType: 'negotiable',
    // Resale details (shown to buyers when forSale)
    saleKmDriven: '',
    saleNoOfOwners: '',
    saleCondition: '',
    saleInsuranceValidTill: '',
    saleRegistrationYear: '',
    saleAccidentFree: true,
    saleRcAvailable: true,
    saleLoanStatus: '',
    minBookingDays: 1,
    outstationPricePerKm: '',
    driverBataPerDay: '',
    // Vehicle-specific fields
    cargoCapacity: '',
    cargoUnit: 'kg',
    helmetIncluded: false,
    engineCC: '',
    acAvailable: false,
    sleeper: false,
    pushback: false,
    withOperator: false,
    operatorChargePerDay: ''
  });

  // Image upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });

  // Plan cap — the backend rejects an over-limit add, so check before showing the form.
  const [planUsage, setPlanUsage] = useState(null);
  useEffect(() => {
    let alive = true;
    carAPI.getAllCars()
      .then((res) => { if (alive) setPlanUsage(res.data?.planUsage || null); })
      .catch(() => { /* the save will still be blocked server-side */ });
    return () => { alive = false; };
  }, []);
  const atPlanLimit = planUsage?.limit != null && planUsage.used >= planUsage.limit;

  // Check verification status for owners
  const isOwner = user?.role === 'owner';
  const verificationStatus = user?.verificationStatus;
  const isVerified = verificationStatus === 'verified';

  // If owner is not verified, show blocking message
  if (isOwner && !isVerified) {
    const statusConfig = {
      pending: {
        icon: Clock,
        iconColor: 'text-yellow-500',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        title: 'Account Verification Pending',
        message: 'Your account is awaiting verification by admin. Once verified, you will be able to add vehicles to your fleet.',
        tip: 'This usually takes 24-48 hours. Please ensure your profile and documents are complete.'
      },
      rejected: {
        icon: XCircle,
        iconColor: 'text-red-500',
        bg: 'bg-red-50',
        border: 'border-red-200',
        title: 'Account Verification Rejected',
        message: 'Your account verification was rejected by admin. Please contact support for more information.',
        tip: 'You may need to update your documents or provide additional information.'
      },
      suspended: {
        icon: ShieldAlert,
        iconColor: 'text-gray-500',
        bg: 'bg-gray-50',
        border: 'border-gray-300',
        title: 'Account Suspended',
        message: 'Your account has been suspended. You cannot add or manage vehicles at this time.',
        tip: 'Please contact support to resolve this issue.'
      }
    };

    const config = statusConfig[verificationStatus] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <div className="space-y-6">
        <PageHeader
          title="Add New Vehicle"
          description="Add a new vehicle to your fleet"
          action={
            <Button variant="outline" onClick={() => navigate('/cars')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Vehicles
            </Button>
          }
        />

        <Card className={`${config.border} border-2`}>
          <CardContent className={`p-8 ${config.bg}`}>
            <div className="flex flex-col items-center text-center">
              <div className={`w-20 h-20 rounded-full ${config.bg} flex items-center justify-center mb-4`}>
                <Icon className={`w-10 h-10 ${config.iconColor}`} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{config.title}</h2>
              <p className="text-gray-600 mb-4 max-w-md">{config.message}</p>
              <p className="text-sm text-gray-500 mb-6">{config.tip}</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
                <Button variant="primary" onClick={() => navigate('/profile')}>
                  View Profile
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Plan limit reached — say so up front rather than after the form is filled in.
  if (atPlanLimit) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Add New Vehicle"
          description="Add a new vehicle to your fleet"
          action={
            <Button variant="outline" onClick={() => navigate('/cars')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Vehicles
            </Button>
          }
        />

        <Card className="border-amber-200 border-2">
          <CardContent className="p-8 bg-amber-50">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                <ShieldAlert className="w-10 h-10 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Vehicle limit reached</h2>
              <p className="text-gray-600 mb-4 max-w-md">
                Your <span className="font-semibold">{planUsage.plan}</span> plan allows{' '}
                <span className="font-semibold">{planUsage.limit}</span> vehicles, and you already have{' '}
                <span className="font-semibold">{planUsage.used}</span>.
              </p>
              <p className="text-sm text-gray-500 mb-6">Upgrade your plan to add more vehicles.</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/cars')}>
                  Back to Vehicles
                </Button>
                <Button variant="primary" onClick={() => navigate('/subscription')}>
                  Upgrade plan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle vehicle type selection
  const handleVehicleTypeChange = (type) => {
    const selectedType = VEHICLE_TYPES.find(v => v.value === type);
    setFormData(prev => ({
      ...prev,
      vehicleType: type,
      vehicleCategory: selectedType?.category || 'four-wheeler',
      seats: selectedType?.seats || 4
    }));
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const picked = Array.from(e.target.files);
    if (picked.length + selectedFiles.length > 20) {
      setToast({
        isOpen: true,
        title: 'Too Many Images',
        message: 'Add up to 20 images per upload (you can add more anytime).',
        type: 'warning'
      });
      return;
    }

    // Compress/resize in the browser before upload so it's fast.
    const files = await Promise.all(picked.map(f => compressImage(f)));
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...files]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  // Handle drag and drop
  const handleDrop = async (e) => {
    e.preventDefault();
    const picked = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );

    if (picked.length + selectedFiles.length > 20) {
      setToast({
        isOpen: true,
        title: 'Too Many Images',
        message: 'Add up to 20 images per upload (you can add more anytime).',
        type: 'warning'
      });
      return;
    }

    const files = await Promise.all(picked.map(f => compressImage(f)));
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...files]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Remove selected image
  const removeImage = (index) => {
    URL.revokeObjectURL(imagePreviews[index].preview);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.make) newErrors.make = 'Make is required';
      if (!formData.model) newErrors.model = 'Model is required';
      if (!formData.year) newErrors.year = 'Year is required';
      else {
        const y = Number(formData.year);
        const thisYear = new Date().getFullYear();
        if (y < 1990 || y > thisYear + 1) newErrors.year = `Enter a year between 1990 and ${thisYear + 1}`;
      }
      if (!formData.registrationNumber) newErrors.registrationNumber = 'Registration number is required';
    } else if (step === 2) {
      if (!formData.seats) newErrors.seats = 'Seats is required';
      if (!formData.city) newErrors.city = 'City is required';
      // Only ask for the price that matches what this vehicle is listed for.
      if (forRent && !formData.pricePerDay) newErrors.pricePerDay = 'Price is required';
      if (forSale && (!formData.salePrice || parseFloat(formData.salePrice) <= 0)) {
        newErrors.salePrice = 'Sale price is required when vehicle is for sale';
      }
      // A vehicle cannot be registered before it was manufactured.
      if (forSale && formData.saleRegistrationYear && formData.year
          && Number(formData.saleRegistrationYear) < Number(formData.year)) {
        newErrors.saleRegistrationYear = `Cannot be earlier than the manufacturing year (${formData.year})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (currentStep < 3) {
      handleNext();
      return;
    }

    if (!validateStep(currentStep)) return;

    try {
      setLoading(true);

      // Transform data to match backend expectations
      const carData = {
        // Vehicle type info
        vehicleType: formData.vehicleType,
        vehicleCategory: formData.vehicleCategory,
        make: formData.make,
        model: formData.model,
        year: parseInt(formData.year),
        registrationNumber: formData.registrationNumber.toUpperCase(),
        seats: parseInt(formData.seats),
        fuelType: formData.fuelType,
        transmission: formData.transmission,
        variant: formData.variant?.trim() || undefined,
        registrationBoard: formData.registrationBoard,
        commercialDetails: ['yellow', 'black'].includes(formData.registrationBoard) ? {
          permitType: formData.permitType || '',
          permitValidTill: formData.permitValidTill || undefined,
          fitnessValidTill: formData.fitnessValidTill || undefined,
          commercialInsurance: !!formData.commercialInsurance
        } : undefined,
        pricePerDay: parseFloat(formData.pricePerDay) || 0,
        pricePerKm: parseFloat(formData.pricePerKm) || 0,
        color: formData.color,
        mileage: parseFloat(formData.mileage) || 0,
        description: formData.description,
        features: formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f) : [],
        images: [],
        isAvailable: formData.isAvailable,
        location: {
          city: formData.city,
          state: formData.state,
          coordinates: (formData.latitude && formData.longitude) ? {
            latitude: formData.latitude,
            longitude: formData.longitude
          } : undefined
        },
        // Service type fields
        serviceTypes: forRent ? formData.serviceTypes : [],
        forRent,
        forSale,
        salePrice: parseFloat(formData.salePrice) || 0,
        salePriceType: formData.salePriceType,
        saleDetails: forSale ? {
          kmDriven: formData.saleKmDriven ? parseInt(formData.saleKmDriven) : undefined,
          noOfOwners: formData.saleNoOfOwners ? parseInt(formData.saleNoOfOwners) : undefined,
          condition: formData.saleCondition || undefined,
          insuranceValidTill: formData.saleInsuranceValidTill || undefined,
          registrationYear: formData.saleRegistrationYear ? parseInt(formData.saleRegistrationYear) : undefined,
          accidentFree: formData.saleAccidentFree,
          rcAvailable: formData.saleRcAvailable,
          loanStatus: formData.saleLoanStatus || ''
        } : undefined,
        minBookingDays: parseInt(formData.minBookingDays) || 1,
        outstationPricePerKm: parseFloat(formData.outstationPricePerKm) || 0,
        driverBataPerDay: parseFloat(formData.driverBataPerDay) || 0,
        // Vehicle-specific details
        vehicleDetails: {
          cargoCapacity: parseFloat(formData.cargoCapacity) || 0,
          cargoUnit: formData.cargoUnit,
          helmetIncluded: formData.helmetIncluded,
          engineCC: parseFloat(formData.engineCC) || 0,
          acAvailable: formData.acAvailable,
          sleeper: formData.sleeper,
          pushback: formData.pushback,
          withOperator: formData.withOperator,
          operatorChargePerDay: parseFloat(formData.operatorChargePerDay) || 0
        }
      };

      // Create car first
      const response = await carAPI.createCar(carData);
      const carId = response.data.car._id;

      // Upload images if any selected
      if (selectedFiles.length > 0) {
        setUploadingImages(true);
        const formDataImages = new FormData();
        selectedFiles.forEach(file => {
          formDataImages.append('images', file);
        });

        try {
          await carAPI.uploadImages(carId, formDataImages);
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          const errorMsg = uploadError.response?.data?.message || uploadError.message || 'Image upload failed';
          setToast({
            isOpen: true,
            title: 'Images Not Uploaded',
            message: `Car created but image upload failed: ${errorMsg}. You can add images later by editing the car.`,
            type: 'warning'
          });
        }
        setUploadingImages(false);
      }

      setToast({
        isOpen: true,
        title: 'Vehicle Added!',
        message: 'The vehicle has been added successfully.',
        type: 'success'
      });
      setTimeout(() => navigate('/cars'), 1500);
    } catch (error) {
      console.error('Error creating car:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: error.response?.data?.message || 'Failed to create car. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Basic Information' },
    { number: 2, title: 'Specifications' },
    { number: 3, title: 'Photos & Details' }
  ];

  // Ask what the vehicle is for before showing the (long) form.
  if (!purpose) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Add New Vehicle"
          description="Add a new vehicle to your fleet"
          action={
            <Button variant="outline" onClick={() => navigate('/cars')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Vehicles
            </Button>
          }
        />

        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-900">What is this vehicle for?</h2>
              <p className="text-sm text-gray-500 mt-1">We'll only ask for the details that apply.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {purposesFor(businessType).map(({ value, title, icon: Icon, blurb }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPurpose(value)}
                  className="group flex flex-col items-center text-center rounded-2xl border-2 border-gray-200 bg-white p-6 transition-all hover:border-blue-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <span className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-3 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                    <Icon className="h-7 w-7" />
                  </span>
                  <span className="font-semibold text-gray-900">{title}</span>
                  <span className="text-xs text-gray-500 mt-1">{blurb}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Add New Vehicle"
        description="Add a new vehicle to your fleet"
        action={
          <Button variant="outline" onClick={() => navigate('/cars')}>
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Vehicles
          </Button>
        }
      />

      {/* Purpose banner — what this vehicle is being listed for */}
      <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
        <span className="text-sm text-gray-700">
          Listing this vehicle for{' '}
          <span className="font-semibold text-gray-900">{purposeTitle(purpose)}</span>
        </span>
        {businessType === 'both' && (
          <button
            type="button"
            onClick={() => setPurpose(null)}
            className="text-sm font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2"
          >
            Change
          </button>
        )}
      </div>

      {/* Step Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step.number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step.number}
                </div>
                <p className="text-sm mt-2 font-medium text-gray-700">{step.title}</p>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-32 h-1 mx-4 ${
                    currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div>
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>

                {/* Vehicle Type Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Vehicle Type
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2">
                    {VEHICLE_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleVehicleTypeChange(type.value)}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          formData.vehicleType === type.value
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-2xl mb-1">{type.icon}</span>
                        <span className={`text-xs font-medium text-center ${
                          formData.vehicleType === type.value ? 'text-blue-700' : 'text-gray-600'
                        }`}>
                          {type.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Selected: <span className="font-medium">{VEHICLE_TYPES.find(v => v.value === formData.vehicleType)?.label}</span>
                    {' '} • Category: <span className="font-medium capitalize">{formData.vehicleCategory.replace('-', ' ')}</span>
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label={formData.vehicleType === 'bike' || formData.vehicleType === 'scooter' ? 'Brand' : 'Make'}
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    error={errors.make}
                    required
                    placeholder={formData.vehicleType === 'bike' ? 'e.g., Honda, Royal Enfield' : 'e.g., Toyota, Tata'}
                  />
                  <Input
                    label="Model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    error={errors.model}
                    required
                    placeholder={formData.vehicleType === 'bike' ? 'e.g., Splendor, Classic 350' :
                                 formData.vehicleType === 'scooter' ? 'e.g., Activa, Jupiter' :
                                 formData.vehicleType === 'truck' ? 'e.g., Tata 407, Eicher Pro' : 'e.g., Nexon, Swift'}
                  />
                  <Input
                    label="Variant"
                    name="variant"
                    value={formData.variant}
                    onChange={handleChange}
                    placeholder={formData.vehicleType === 'bike' ? 'e.g., Classic 350 Chrome' : 'e.g., 2.4 GX 7-STR'}
                  />
                  <Input
                    label="Year"
                    name="year"
                    type="number"
                    value={formData.year}
                    onChange={handleChange}
                    error={errors.year}
                    required
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                  <Input
                    label="Registration Number"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    error={errors.registrationNumber}
                    required
                    placeholder="e.g., KA-01-AB-1234"
                  />
                  <Select
                    label="Registration Board"
                    name="registrationBoard"
                    value={formData.registrationBoard}
                    onChange={handleChange}
                  >
                    <option value="white">White — Private</option>
                    <option value="yellow">Yellow — Commercial</option>
                    <option value="green">Green — Electric (EV)</option>
                    <option value="black">Black — Self-drive Rental</option>
                  </Select>
                  {forRent && ['yellow', 'black'].includes(formData.registrationBoard) && (
                    <>
                      <Select
                        label="Permit Type"
                        name="permitType"
                        value={formData.permitType}
                        onChange={handleChange}
                      >
                        <option value="">Select permit</option>
                        <option value="state">State</option>
                        <option value="national">National</option>
                        <option value="all-india">All-India</option>
                        <option value="contract-carriage">Contract Carriage</option>
                      </Select>
                      <Input
                        label="Permit Valid Till"
                        name="permitValidTill"
                        type="date"
                        value={formData.permitValidTill}
                        onChange={handleChange}
                      />
                      <Input
                        label="Fitness Valid Till"
                        name="fitnessValidTill"
                        type="date"
                        value={formData.fitnessValidTill}
                        onChange={handleChange}
                      />
                      <Select
                        label="Commercial Insurance"
                        name="commercialInsurance"
                        value={formData.commercialInsurance ? 'yes' : 'no'}
                        onChange={(e) => setFormData(prev => ({ ...prev, commercialInsurance: e.target.value === 'yes' }))}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </Select>
                    </>
                  )}
                  <Input
                    label="Color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="e.g., Black"
                  />
                  <Input
                    label="Mileage (km/l)"
                    name="mileage"
                    type="number"
                    value={formData.mileage}
                    onChange={handleChange}
                    placeholder="e.g., 15"
                  />
                </div>

                {/* Vehicle-Specific Fields */}
                {/* Two-Wheeler Fields (Bike/Scooter) */}
                {(formData.vehicleType === 'bike' || formData.vehicleType === 'scooter') && (
                  <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="text-sm font-semibold text-orange-800 mb-3">🏍️ Two-Wheeler Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Engine CC"
                        name="engineCC"
                        type="number"
                        value={formData.engineCC}
                        onChange={handleChange}
                        placeholder="e.g., 150, 350"
                      />
                      <label className="flex items-center space-x-2 p-3 border rounded-lg bg-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.helmetIncluded}
                          onChange={(e) => setFormData(prev => ({ ...prev, helmetIncluded: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">Helmet Included</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Commercial Vehicle Fields (Truck, Van, Pickup) */}
                {(formData.vehicleType === 'truck' || formData.vehicleType === 'van' || formData.vehicleType === 'pickup') && (
                  <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-3">🚛 Cargo Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Cargo Capacity"
                        name="cargoCapacity"
                        type="number"
                        value={formData.cargoCapacity}
                        onChange={handleChange}
                        placeholder="e.g., 1000"
                      />
                      <Select
                        label="Capacity Unit"
                        name="cargoUnit"
                        value={formData.cargoUnit}
                        onChange={handleChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="ton">Tons</option>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Bus/Tempo Traveller Fields */}
                {(formData.vehicleType === 'bus' || formData.vehicleType === 'tempo-traveller') && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3">🚌 Passenger Bus Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <label className="flex items-center space-x-2 p-3 border rounded-lg bg-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.acAvailable}
                          onChange={(e) => setFormData(prev => ({ ...prev, acAvailable: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">AC Available</span>
                      </label>
                      <label className="flex items-center space-x-2 p-3 border rounded-lg bg-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.sleeper}
                          onChange={(e) => setFormData(prev => ({ ...prev, sleeper: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">Sleeper</span>
                      </label>
                      <label className="flex items-center space-x-2 p-3 border rounded-lg bg-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.pushback}
                          onChange={(e) => setFormData(prev => ({ ...prev, pushback: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">Pushback Seats</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Special Vehicle Fields (JCB, Tractor) */}
                {(formData.vehicleType === 'jcb' || formData.vehicleType === 'tractor') && (
                  <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="text-sm font-semibold text-purple-800 mb-3">🏗️ Special Vehicle Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center space-x-2 p-3 border rounded-lg bg-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.withOperator}
                          onChange={(e) => setFormData(prev => ({ ...prev, withOperator: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">Comes with Operator</span>
                      </label>
                      {formData.withOperator && (
                        <Input
                          label="Operator Charge Per Day (₹)"
                          name="operatorChargePerDay"
                          type="number"
                          value={formData.operatorChargePerDay}
                          onChange={handleChange}
                          placeholder="e.g., 800"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Specifications */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Seating Capacity"
                    name="seats"
                    type="number"
                    value={formData.seats}
                    onChange={handleChange}
                    error={errors.seats}
                    required
                    min="1"
                    max={formData.vehicleType === 'bus' ? '60' : formData.vehicleType === 'tempo-traveller' ? '26' : '20'}
                  />
                  <Select
                    label="Fuel Type"
                    name="fuelType"
                    value={formData.fuelType}
                    onChange={handleChange}
                    required
                  >
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="CNG">CNG</option>
                  </Select>
                  <Select
                    label="Transmission"
                    name="transmission"
                    value={formData.transmission}
                    onChange={handleChange}
                    required
                  >
                    <option value="Manual">Manual</option>
                    <option value="Automatic">Automatic</option>
                  </Select>
                  {forRent && (
                    <>
                      <Input
                        label="Price Per Day (₹)"
                        name="pricePerDay"
                        type="number"
                        value={formData.pricePerDay}
                        onChange={handleChange}
                        error={errors.pricePerDay}
                        required
                        min="0"
                        placeholder="e.g., 1500"
                      />
                      <Input
                        label="Price Per KM (₹)"
                        name="pricePerKm"
                        type="number"
                        value={formData.pricePerKm}
                        onChange={handleChange}
                        min="0"
                        placeholder="e.g., 12"
                      />
                    </>
                  )}
                  <div className="col-span-1 sm:col-span-2">
                    <AddressAutocomplete
                      label="Search Location"
                      placeholder="Search vehicle location..."
                      onChange={(loc) => {
                        setFormData(prev => ({
                          ...prev,
                          city: loc.city || '',
                          state: loc.state || '',
                          latitude: loc.lat || null,
                          longitude: loc.lng || null
                        }));
                      }}
                    />
                    <p className="text-xs text-orange-500 mt-1 font-medium">Customers will find this vehicle based on this location. Please provide the correct parking/garage address.</p>
                  </div>
                  <Input
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    error={errors.city}
                    required
                    placeholder="e.g., Bangalore"
                  />
                  <Input
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="e.g., Karnataka"
                  />
                  <Select
                    label="Availability"
                    name="isAvailable"
                    value={formData.isAvailable}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.value === 'true' }))}
                    required
                  >
                    <option value="true">Available</option>
                    <option value="false">Not Available</option>
                  </Select>
                </div>

                {/* Service Types */}
                {forRent && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Types (Select all that apply)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                      { value: 'rental', label: 'Multi-Day Rental' },
                      { value: 'daily', label: 'Daily Rental' },
                      { value: 'km-based', label: 'Pay Per KM' },
                      { value: 'outstation', label: 'Outstation' },
                      { value: 'local', label: 'Local Trips' }
                    ].map((type) => (
                      <label key={type.value} className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.serviceTypes.includes(type.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, serviceTypes: [...prev.serviceTypes, type.value] }));
                            } else {
                              setFormData(prev => ({ ...prev, serviceTypes: prev.serviceTypes.filter(t => t !== type.value) }));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                )}

                {/* Additional Pricing for specific services */}
                {forRent && (formData.serviceTypes.includes('outstation') || formData.serviceTypes.includes('km-based')) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg mt-4">
                    <Input
                      label="Outstation Price Per KM (₹)"
                      name="outstationPricePerKm"
                      type="number"
                      value={formData.outstationPricePerKm}
                      onChange={handleChange}
                      min="0"
                      placeholder="e.g., 14"
                    />
                    <Input
                      label="Driver Bata Per Day (₹)"
                      name="driverBataPerDay"
                      type="number"
                      value={formData.driverBataPerDay}
                      onChange={handleChange}
                      min="0"
                      placeholder="e.g., 400"
                    />
                  </div>
                )}

                {/* Sale details — shown when this vehicle is listed for sale */}
                {forSale && (
                <div className="mt-4 p-4 border rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-900">Sale Details</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Buyers see this information on the listing.</p>
                    <div className="mt-3 space-y-4">
                      <Input
                        label="Sale Price (₹)"
                        name="salePrice"
                        type="number"
                        value={formData.salePrice}
                        onChange={handleChange}
                        error={errors.salePrice}
                        required
                        min="0"
                        placeholder="e.g., 500000"
                      />
                      {formData.salePrice && parseFloat(formData.salePrice) > 0 && (
                        <p className="mt-1 text-sm text-green-600 font-medium">
                          {numberToIndianWords(parseFloat(formData.salePrice))}
                        </p>
                      )}

                      {/* Sale Price Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price Type
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {[
                            { value: 'fixed', label: 'Fixed Price', icon: '💰', desc: 'Price is final' },
                            { value: 'negotiable', label: 'Negotiable', icon: '🤝', desc: 'Open to offers' },
                            { value: 'best-offer', label: 'Best Offer', icon: '🏷️', desc: 'Highest bid wins' },
                            { value: 'contact-for-price', label: 'Contact for Price', icon: '📞', desc: 'Call to discuss' }
                          ].map((type) => (
                            <label
                              key={type.value}
                              className={`flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                                formData.salePriceType === type.value
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="salePriceType"
                                value={type.value}
                                checked={formData.salePriceType === type.value}
                                onChange={handleChange}
                                className="sr-only"
                              />
                              <span className="text-2xl mb-1">{type.icon}</span>
                              <span className="text-sm font-medium text-gray-900">{type.label}</span>
                              <span className="text-xs text-gray-500">{type.desc}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Resale Details — shown to buyers */}
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        <p className="text-sm font-semibold text-gray-800 mb-3">Resale Details <span className="font-normal text-gray-400">(shown to buyers)</span></p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Input
                            label="Odometer / KM Driven"
                            name="saleKmDriven"
                            type="number"
                            placeholder="e.g., 45000"
                            value={formData.saleKmDriven}
                            onChange={handleChange}
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Owners</label>
                            <select
                              name="saleNoOfOwners"
                              value={formData.saleNoOfOwners}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select</option>
                              {OWNER_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                            <select
                              name="saleCondition"
                              value={formData.saleCondition}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select</option>
                              <option value="excellent">Excellent</option>
                              <option value="good">Good</option>
                              <option value="fair">Fair</option>
                              <option value="needs-work">Needs Work</option>
                            </select>
                          </div>
                          <Input
                            label="Registration Year"
                            name="saleRegistrationYear"
                            type="number"
                            placeholder="e.g., 2019"
                            value={formData.saleRegistrationYear}
                            onChange={handleChange}
                            error={errors.saleRegistrationYear}
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Valid Till</label>
                            <input
                              type="date"
                              name="saleInsuranceValidTill"
                              value={formData.saleInsuranceValidTill}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Loan / Hypothecation</label>
                            <select
                              name="saleLoanStatus"
                              value={formData.saleLoanStatus}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Prefer not to say</option>
                              <option value="none">No loan taken</option>
                              <option value="closed">Loan closed — NOC available</option>
                              <option value="active">Loan running — NOC pending</option>
                            </select>
                            <p className="text-[11px] text-gray-400 mt-1">Buyers cannot transfer the RC until the bank issues an NOC.</p>
                          </div>
                          <div className="flex items-center gap-4 pt-6">
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                              <input type="checkbox" checked={formData.saleAccidentFree} onChange={(e) => setFormData(prev => ({ ...prev, saleAccidentFree: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded border-gray-300" />
                              Accident-free
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                              <input type="checkbox" checked={formData.saleRcAvailable} onChange={(e) => setFormData(prev => ({ ...prev, saleRcAvailable: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded border-gray-300" />
                              RC available
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
                )}
              </div>
            )}

            {/* Step 3: Photos & Additional Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Photos & Details</h3>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Photos (add up to 20 per upload, more anytime)
                  </label>

                  {/* Drag and Drop Zone */}
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">
                      Drag and drop images here, or click to select
                    </p>
                    <p className="text-sm text-gray-400">
                      Supports: JPG, PNG, WebP (Max 5MB each)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>

                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                      {imagePreviews.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img.preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <p className="text-xs text-gray-500 mt-1 truncate">{img.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features
                  </label>
                  {/* Preset feature chips */}
                  {(() => {
                    const presets = {
                      car: ['AC', 'Power Steering', 'Power Windows', 'Central Locking', 'Airbags', 'ABS', 'GPS', 'Bluetooth', 'Sunroof', 'Rear Camera', 'Alloy Wheels', 'Music System', 'USB Charging', 'Cruise Control', 'Leather Seats', 'Parking Sensors', 'Fog Lights', 'Keyless Entry'],
                      suv: ['AC', 'Power Steering', 'Power Windows', 'Central Locking', 'Airbags', 'ABS', 'GPS', 'Bluetooth', 'Sunroof', 'Rear Camera', '4x4', 'Alloy Wheels', 'Roof Rails', 'Hill Assist', 'Leather Seats', 'Third Row Seats', '7 Seater'],
                      bike: ['ABS', 'Disc Brake', 'LED Lights', 'Digital Meter', 'USB Charging', 'Tubeless Tyres', 'Alloy Wheels', 'Pillion Seat', 'Helmet Included', 'Phone Mount'],
                      scooter: ['Disc Brake', 'LED Lights', 'Digital Meter', 'USB Charging', 'Tubeless Tyres', 'Under Seat Storage', 'Helmet Included', 'Phone Mount', 'Alloy Wheels'],
                      auto: ['Meter', 'Rain Cover', 'Music System', 'Phone Charging', 'GPS'],
                      bus: ['AC', 'Non-AC', 'Pushback Seats', 'Sleeper', 'Music System', 'USB Charging', 'Curtains', 'First Aid', 'Fire Extinguisher', 'CCTV', 'WiFi', 'TV Screen'],
                      'tempo-traveller': ['AC', 'Non-AC', 'Pushback Seats', 'Music System', 'USB Charging', 'Curtains', 'Ice Box', 'First Aid'],
                      truck: ['AC', 'Power Steering', 'GPS Tracking', 'Tarpaulin', 'Hydraulic Lift', 'Container Body', 'Open Body'],
                      van: ['AC', 'Power Steering', 'Sliding Door', 'Rear Camera', 'GPS', 'Music System'],
                    };
                    const allPresets = presets[formData.vehicleType] || presets.car;
                    const selectedFeatures = formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f) : [];

                    const toggleFeature = (feature) => {
                      let updated;
                      if (selectedFeatures.includes(feature)) {
                        updated = selectedFeatures.filter(f => f !== feature);
                      } else {
                        updated = [...selectedFeatures, feature];
                      }
                      setFormData(prev => ({ ...prev, features: updated.join(', ') }));
                    };

                    return (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {allPresets.map((feature) => (
                            <button
                              key={feature}
                              type="button"
                              onClick={() => toggleFeature(feature)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                selectedFeatures.includes(feature)
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              {selectedFeatures.includes(feature) ? '✓ ' : ''}{feature}
                            </button>
                          ))}
                        </div>
                        {/* Selected chips */}
                        {selectedFeatures.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {selectedFeatures.filter(f => !allPresets.includes(f)).map((feature) => (
                              <span
                                key={feature}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                              >
                                {feature}
                                <button type="button" onClick={() => toggleFeature(feature)} className="hover:text-red-500">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Custom feature input */}
                        <input
                          type="text"
                          placeholder="Type custom feature and press Enter..."
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              const val = e.target.value.trim().replace(/,$/, '');
                              if (val && !selectedFeatures.includes(val)) {
                                const updated = [...selectedFeatures, val];
                                setFormData(prev => ({ ...prev, features: updated.join(', ') }));
                              }
                              e.target.value = '';
                            }
                          }}
                        />
                        {selectedFeatures.length > 0 && (
                          <p className="text-xs text-gray-400">{selectedFeatures.length} features selected</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the car..."
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            Back
          </Button>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/cars')}
            >
              Cancel
            </Button>
            {currentStep < 3 ? (
              <Button type="button" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={loading || uploadingImages}>
                {loading || uploadingImages ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {uploadingImages ? 'Uploading Images...' : 'Creating...'}
                  </>
                ) : (
                  'Create Vehicle'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ToastModal
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default AddCar;
